import { useEffect, useMemo, useState } from 'react';

export interface MapFocusPoint {
  lat: number;
  lng: number;
}

export interface MapFocus {
  coordinates: MapFocusPoint;
  /** A store is framed close enough to read its streets; the guest is only centered. */
  isSelectedStore: boolean;
}

interface UseMapFocusArgs {
  /** The guest's position: their fix, or the address they searched. */
  guestCoordinates?: MapFocusPoint | null;
  /**
   * Bumped on every address search, so one that lands on the coordinates the guest already
   * has — the same address again — still brings the map back from a store.
   */
  guestFocusVersion?: number;
  /** The selected store, once we know where it is. */
  selectedCoordinates?: MapFocusPoint | null;
  /** True while the selected store is still loading, so we don't know where it is yet. */
  isSelectionLoading?: boolean;
  /** True while the permission check or the guest's fix is still settling. */
  isLocatingGuest?: boolean;
}

interface UseMapFocusResult {
  /** Where the map belongs, or null while we know of nowhere to put it. */
  focus: MapFocus | null;
  /**
   * Whether the map may be created yet. A map keeps the frame it was created with, so one
   * created while a selected store is still loading opens on the guest and then slides off
   * them — the jump a guest arriving on a store's link would otherwise be shown. The same
   * goes for one created before the guest's fix lands, which opens on the region's default.
   */
  canOpenMap: boolean;
}

/**
 * Where the map should be pointed.
 *
 * Two things move the camera and they disagree. The guest's position moves it whenever it
 * changes — the first fix, a recenter, an address search — and so does the store they pick.
 * A selected store wins, because it is what the guest is looking at: a fix that lands after
 * a deep link into store details must not pull the map off that store.
 *
 * Dismissing a store leaves the map where it is. The guest closed a store; they did not ask
 * to be taken back to themselves.
 */
export function useMapFocus({
  guestCoordinates,
  guestFocusVersion,
  selectedCoordinates,
  isSelectionLoading = false,
  isLocatingGuest = false,
}: UseMapFocusArgs): UseMapFocusResult {
  const selectedFocus = useMemo(
    () => toFocus(selectedCoordinates, true),
    // The object is rebuilt every render, so follow the coordinates themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCoordinates?.lat, selectedCoordinates?.lng]
  );
  const [focus, setFocus] = useState<MapFocus | null>(
    () => selectedFocus ?? toFocus(guestCoordinates, false)
  );

  useEffect(() => {
    if (selectedFocus) {
      setFocus(selectedFocus);
    }
  }, [selectedFocus]);

  useEffect(() => {
    // Deliberately keyed on the guest's coordinates and searches alone: this has to run when
    // they move or search, and must not run again when a store is dismissed.
    if (guestCoordinates && !selectedCoordinates) {
      setFocus(toFocus(guestCoordinates, false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestCoordinates?.lat, guestCoordinates?.lng, guestFocusVersion]);

  // Latched: the map is only ever held back from opening, never torn down and rebuilt for a
  // store that has to be fetched later. Moving the camera covers that case.
  // Only a map with nowhere to go waits on the guest's fix; a denied or failed one lets it open
  // on the default.
  const isWaitingForFocus = isSelectionLoading || (isLocatingGuest && !focus);
  const [canOpenMap, setCanOpenMap] = useState(!isWaitingForFocus);

  useEffect(() => {
    if (!isWaitingForFocus) {
      setCanOpenMap(true);
    }
  }, [isWaitingForFocus]);

  // Selection and its repeat-tap version change in the same render. Expose the new store
  // immediately so that version cannot trigger a camera move to the previous focus first.
  // State remembers it after dismissal, which intentionally leaves the map where it was.
  return { focus: selectedFocus ?? focus, canOpenMap };
}

function toFocus(
  point: MapFocusPoint | null | undefined,
  isSelectedStore: boolean
): MapFocus | null {
  return point ? { coordinates: { lat: point.lat, lng: point.lng }, isSelectedStore } : null;
}
